/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import moment from 'moment'

/**
 * Format date using various formats
 * @param date - Date to format
 * @param format - Format type ('shortDate', 'longDate', 'dateTime', etc.) or moment format string
 */
export function formatDate(
  date: Date | moment.Moment | number | null | undefined,
  format: string = 'shortDate'
): string {
  if (!date) return ''

  const dateMoment = moment.isMoment(date) ? date : moment(date)

  if (!dateMoment.isValid()) return ''

  // Predefined formats matching legacy app
  const formats: Record<string, string> = {
    shortDate: 'DD-MMM-YYYY',
    longDate: 'DD-MMMM-YYYY',
    dateTime: 'DD-MMM-YYYY HH:mm:ss',
    time: 'HH:mm:ss',
    apiDate: 'YYYYMMDD'
  }

  const formatString = formats[format] || format
  return dateMoment.format(formatString)
}

/**
 * Format number with thousands separator
 * @param num - Number to format
 * @param decimals - Number of decimal places (default: 0)
 */
export function formatNumber(num: number | null | undefined, decimals: number = 0): string {
  if (num === null || num === undefined) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
}

/**
 * Format file size in human readable format
 * @param bytes - Size in bytes
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return ''
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Truncate string with ellipsis
 * @param str - String to truncate
 * @param maxLength - Maximum length
 */
export function truncate(str: string | null | undefined, maxLength: number = 50): string {
  if (!str) return ''
  if (str.length <= maxLength) return str
  return str.substring(0, maxLength) + '...'
}
